import React from 'react';
import NumberFormat, { NumberFormatValues } from 'react-number-format';
import { FilledTextFieldProps, TextFieldProps } from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';
import { TextField } from '../text-field';
import './number-field.scss';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface INumericValue extends NumberFormatValues {}

export interface INumberFieldProps {
  children?: FilledTextFieldProps['children'];
  className?: FilledTextFieldProps['className'];
  disabled?: boolean;
  error?: boolean;
  errorText?: FilledTextFieldProps['helperText'];
  fullWidth?: boolean;
  helperText?: FilledTextFieldProps['helperText'];
  InputLabelProps?: FilledTextFieldProps['InputLabelProps'];
  inputProps?: FilledTextFieldProps['inputProps'];
  inputRef?: FilledTextFieldProps['inputRef'];
  label?: FilledTextFieldProps['label'];
  name?: string;
  onChange?: (values: INumericValue) => void;
  readOnly?: boolean;
  required?: boolean;
  value?: string | number;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  allowNegative?: boolean;
  decimalSeparator?: string;
  thousandSeparator?: string;
  filters?: Array<(values: INumericValue) => boolean>;
  precision?: number;
  multiline?: boolean;
  variant?: TextFieldProps['variant'] | FilledTextFieldProps['variant'];
  placeholder?: string;
  InputProps?: TextFieldProps['InputProps'];
}

export type IIntegerFieldProps = Omit<INumberFieldProps, 'decimalSeparator' | 'precision'>;

export function IntegerField(props: IIntegerFieldProps) {
  const {
    children,
    className,
    disabled,
    error,
    errorText,
    fullWidth,
    helperText,
    InputLabelProps,
    inputProps,
    inputRef,
    label,
    name,
    onChange,
    readOnly,
    required,
    value,
    onBlur,
    allowNegative,
    thousandSeparator,
    filters,
    multiline,
    variant,
    placeholder,
    InputProps,
  } = props;

  return (
    <NumberField
      decimalSeparator={undefined}
      precision={0}
      children={children}
      className={className}
      disabled={disabled}
      error={error}
      errorText={errorText}
      fullWidth={fullWidth}
      helperText={helperText}
      InputLabelProps={InputLabelProps}
      inputProps={inputProps}
      inputRef={inputRef}
      label={label}
      name={name}
      onChange={onChange}
      readOnly={readOnly}
      required={required}
      value={value}
      onBlur={onBlur}
      allowNegative={allowNegative}
      thousandSeparator={thousandSeparator}
      filters={filters}
      multiline={multiline}
      variant={variant}
      placeholder={placeholder}
      InputProps={InputProps}
    />
  );
}

const useStyles = makeStyles()({
  root: {
    minWidth: 0,
    width: 60,
  },
  input: {
    padding: '6px 5px 7px 6px',
    minWidth: '20px',
  },
});

export function SmallIntegerField(props: IIntegerFieldProps) {
  const {
    placeholder,
    children,
    className,
    disabled,
    error,
    errorText,
    fullWidth,
    helperText,
    InputLabelProps,
    inputProps,
    inputRef,
    label,
    name,
    onChange,
    readOnly,
    required,
    value,
    onBlur,
    allowNegative,
    thousandSeparator,
    filters,
    variant,
  } = props;
  const { classes } = useStyles();

  return (
    <NumberField
      inputProps={{ ...inputProps, placeholder }}
      decimalSeparator={undefined}
      precision={0}
      children={children}
      className={className}
      disabled={disabled}
      error={error}
      errorText={errorText}
      fullWidth={fullWidth}
      helperText={helperText}
      InputLabelProps={InputLabelProps}
      inputRef={inputRef}
      label={label}
      name={name}
      onChange={onChange}
      readOnly={readOnly}
      required={required}
      value={value}
      onBlur={onBlur}
      allowNegative={allowNegative}
      thousandSeparator={thousandSeparator}
      filters={filters}
      multiline={false}
      variant={variant}
      InputProps={{ classes: { root: classes.root, input: classes.input } }}
    />
  );
}

export function DecimalField(props: INumberFieldProps) {
  const {
    children,
    className,
    disabled,
    error,
    errorText,
    fullWidth,
    helperText,
    InputLabelProps,
    inputProps,
    inputRef,
    label,
    name,
    onChange,
    readOnly,
    required,
    value,
    onBlur,
    allowNegative,
    decimalSeparator,
    thousandSeparator,
    filters,
    precision,
    multiline,
    variant,
    placeholder,
    InputProps,
  } = props;

  return (
    <NumberField
      children={children}
      className={className}
      disabled={disabled}
      error={error}
      errorText={errorText}
      fullWidth={fullWidth}
      helperText={helperText}
      InputLabelProps={InputLabelProps}
      inputProps={inputProps}
      inputRef={inputRef}
      label={label}
      name={name}
      onChange={onChange}
      readOnly={readOnly}
      required={required}
      value={value}
      onBlur={onBlur}
      allowNegative={allowNegative}
      decimalSeparator={decimalSeparator}
      thousandSeparator={thousandSeparator}
      filters={filters}
      precision={precision}
      multiline={multiline}
      variant={variant}
      placeholder={placeholder}
      InputProps={InputProps}
    />
  );
}

export function NumberField(props: INumberFieldProps) {
  const {
    filters,
    precision = 2,
    onChange,
    onBlur,
    children,
    className,
    disabled,
    error,
    errorText,
    fullWidth,
    helperText,
    InputLabelProps,
    inputProps,
    inputRef,
    label,
    name,
    readOnly,
    required,
    value,
    allowNegative = true,
    decimalSeparator = ',',
    thousandSeparator = '.',
    multiline,
    variant,
    placeholder,
    InputProps,
  } = props;

  function handleBlur(e: any) {
    onBlur &&
      onBlur({
        target: {
          value: e.target.value.replace(/\./g, ''),
        },
      } as any);
  }
  const applyFilters = (values: NumberFormatValues): boolean => {
    let result = true;
    if (filters) {
      filters.forEach(filter => {
        result = result && filter(values);
      });
    }
    return result;
  };

  return (
    <NumberFormat
      customInput={TextField}
      isAllowed={applyFilters}
      decimalScale={precision}
      onBlur={handleBlur}
      onValueChange={onChange}
      children={children}
      className={className}
      disabled={disabled}
      error={error}
      errorText={errorText}
      fullWidth={fullWidth}
      helperText={helperText}
      InputLabelProps={InputLabelProps}
      inputProps={inputProps}
      inputRef={inputRef}
      label={label}
      name={name}
      readOnly={readOnly}
      required={required}
      value={value}
      allowNegative={allowNegative}
      decimalSeparator={decimalSeparator}
      thousandSeparator={thousandSeparator}
      multiline={multiline}
      variant={variant}
      placeholder={placeholder}
      InputProps={InputProps}
    />
  );
}

/* Danish culture specification
    http://std.dkuug.dk/cultreg/registrations/narrative/da_DK,_4.3.html
 */
